import Button from '../Button/Button'
import classes from './ConfirmActionDialog.module.scss'
import {
  Color,
  Header,
  Paragraph
} from '../..'
import type {
  FormEvent,
  ReactNode
} from 'react'

// IDs are for pairing the role=dialog of react-modal
// with accessible description content.
export interface BaseProps {
  actionText: string
  message: ReactNode
  onCancel: () => void
  onConfirm: () => void
  title: ReactNode
  variant?: 'danger' | 'primary' | 'success' | 'secondary'
  isFormSubmit?: boolean
  ariaLabel?: string
  cancelText?: string
}

export interface WithSecondaryAction {
  secondaryActionText: string
  onSecondaryAction: () => void
}

export interface WithoutSecondaryAction {
  secondaryActionText?: undefined
  onSecondaryAction?: undefined
}

type Props = BaseProps & (WithSecondaryAction | WithoutSecondaryAction)

const ConfirmActionDialog = ({
  actionText,
  message,
  onCancel,
  onConfirm,
  title,
  ariaLabel,
  variant = 'danger',
  isFormSubmit = true,
  cancelText = 'Cancel',
  secondaryActionText,
  onSecondaryAction
}: Props) => {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    onConfirm()
  }

  const renderDialog = () =>
    <div
      aria-label={ariaLabel}
      className={classes.ConfirmActionDialog}
    >
      <div className={classes.TextDialog}>
        <div id='modal-label'>
          <Header
            hasNoTopMargin
            lessWeight
            rank={3}
          >
            {title}
          </Header>
        </div>
        <div id='modal-description'>
          <Paragraph>
            <strong>
              <Color variant='theme-filter-icon'>
                {message}
              </Color>
            </strong>
          </Paragraph>
        </div>
      </div>
      {
        secondaryActionText
          ? (
            <div className={classes.secondaryButtonWrapper}>
              <Button
                onClick={onCancel}
                variant='secondary'
              >
                {cancelText}
              </Button>
              <div className={classes.rightButtons}>
                <Button
                  onClick={
                    isFormSubmit
                      ? undefined
                      : onConfirm
                  }
                  type={
                    isFormSubmit
                      ? 'submit'
                      : 'button'
                  }
                  variant={variant}
                >
                  {actionText}
                </Button>
                <Button
                  onClick={onSecondaryAction}
                  variant='primary'
                >
                  {secondaryActionText}
                </Button>
              </div>
            </div>
          )
          : (
            <div className={classes.buttonWrapper}>
              <Button
                onClick={onCancel}
                variant='secondary'
              >
                {cancelText}
              </Button>
              <Button
                onClick={
                  isFormSubmit
                    ? undefined
                    : onConfirm
                }
                type={
                  isFormSubmit
                    ? 'submit'
                    : 'button'
                }
                variant={variant}
              >
                {actionText}
              </Button>
            </div>
          )
      }
    </div>

  return (
    isFormSubmit
      ? (
        <form onSubmit={handleSubmit}>
          {renderDialog()}
        </form>
      )
      : renderDialog()
  )
}

export default ConfirmActionDialog
